import React from "react";
import styled from "styled-components";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const HabitChart = ({ habits = [] }) => {
  // Helper function to get the range of dates to display
  const getChartDates = (habits) => {
    // Extract all completed dates from habits
    const completedDates = Object.keys(
      habits.reduce((acc, habit) => {
        if (habit.completedDates) {
          Object.keys(habit.completedDates).forEach((date) => {
            acc[date] = true; // Store unique completed dates
          });
        }
        return acc;
      }, {})
    );

    if (completedDates.length === 0) return []; // No completed dates

    // Sort dates in ascending order
    completedDates.sort((a, b) => new Date(a) - new Date(b));

    // Start from the first completed date
    const startDate = new Date(completedDates[0]);
    const today = new Date();
    const maxDays = 30;

    // Generate up to 30 dates from the start date
    const dates = [];
    for (let i = 0; i < maxDays; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      if (currentDate > today) break; // Stop if the date exceeds today
      dates.push(currentDate.toISOString().split("T")[0]); // Format as YYYY-MM-DD
    }

    return dates;
  };

  // Generate dates for the chart
  const chartDates = getChartDates(habits);

  // Calculate the total number of completed habits for each date
  const habitCounts = chartDates.map((date) => {
    return habits.reduce((count, habit) => {
      if (habit.completedDates?.[date]) {
        return count + 1;
      }
      return count;
    }, 0);
  });

  // Define chart data and labels
  const chartData = {
    labels: chartDates, // Display only relevant dates
    datasets: [
      {
        label: "Habits Completed",
        data: habitCounts, // Y-axis is the total completed count for each date
        borderColor: "#5ac18e",
        backgroundColor: "rgba(90, 193, 142, 0.2)",
        pointBackgroundColor: "#5ac18e",
        tension: 0.3, // Smooth curve
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw} habits completed`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
        title: {
          display: true,
          text: "Number of Habits Completed",
          color: "#ffffff",
        },
      },
      x: {
        ticks: {
          maxTicksLimit: 10, // Limit the number of displayed dates on the x-axis
        },
        title: {
          display: true,
          text: "Date",
          color: "#ffffff",
        },
      },
    },
  };

  return (
    <ChartContainer>
      {chartDates.length === 0 ? (
        <h3>No habits completed yet!</h3>
      ) : (
        <>
          <h3>Habit Completion Over Time</h3>
          <Line data={chartData} options={chartOptions} />
        </>
      )}
    </ChartContainer>
  );
};

// Styled container
const ChartContainer = styled.div`
  margin-top: 2em;
  padding: 20px;
  background: #1f2637;
  border-radius: 15px;
  color: white;

  h3 {
    text-align: center;
    font-weight: 300;
    margin-bottom: 20px;
  }
`;

export default HabitChart;
